import { useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  fetchVapidKey,
  subscribePush,
  unsubscribePush,
} from "@/services/notificationApi";
import {
  fetchBillDisplayName,
  updateBillDisplayName,
  type BillDisplayName,
} from "@/services/userApi";
import { usePushSubscription } from "@/lib/push";
import { INPUT_CLASS } from "../types/settings.types";

const PUSH_BINDING = {
  fetchKey: fetchVapidKey,
  register: subscribePush,
  unregister: unsubscribePush,
};

const STATE_HINT = {
  unsupported: "This browser can't receive push notifications.",
  "needs-install":
    "On iPhone, tap Share → Add to Home Screen, then open BOO from the home-screen icon and enable notifications here.",
  unavailable: "Push isn't set up on the server yet.",
  blocked:
    "Notifications are blocked. Allow them in your browser's site settings.",
} as const;

export default function BillNotificationsCard() {
  const { state, enable, disable } = usePushSubscription(PUSH_BINDING);
  const [busy, setBusy] = useState(false);
  const [display, setDisplay] = useState<BillDisplayName | null>(null);
  const [name, setName] = useState("");

  useEffect(() => {
    async function loadDisplayName() {
      try {
        const d = await fetchBillDisplayName();
        setDisplay(d);
        setName(d.name ?? "");
      } catch {
        toast.error("Failed to load display name");
      }
    }
    void loadDisplayName();
  }, []);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          <Bell className="w-4 h-4" /> Friends &amp; notifications
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground" htmlFor="bill-name">
            Name shown to friends
          </label>
          <div className="flex gap-2">
            <input
              id="bill-name"
              value={name}
              maxLength={40}
              placeholder={display?.fallback ?? "Your name"}
              onChange={(e) => setName(e.target.value)}
              className={`${INPUT_CLASS} min-w-0 flex-1`}
            />
            <Button
              size="sm"
              disabled={busy || name.trim() === (display?.name ?? "")}
              onClick={() =>
                run(async () => {
                  const next = await updateBillDisplayName(name.trim());
                  setDisplay(next);
                  setName(next.name ?? "");
                  toast.success("Saved");
                })
              }
            >
              Save
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Replaces &ldquo;Me&rdquo; on the page friends open. Leave empty to
            use your account name.
          </p>
        </div>

        <div className="space-y-1.5 border-t border-border pt-4">
          <div className="text-xs text-muted-foreground">
            Push notifications on this device
          </div>
          {state === "loading" && (
            <p className="text-xs text-muted-foreground">Checking…</p>
          )}
          {state === "off" && (
            <Button size="sm" loading={busy} onClick={() => run(enable)}>
              Turn on notifications
            </Button>
          )}
          {state === "on" && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-foreground">On</span>
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => run(disable)}
              >
                Turn off
              </Button>
            </div>
          )}
          {state !== "loading" && state !== "off" && state !== "on" && (
            <p className="text-xs text-muted-foreground">{STATE_HINT[state]}</p>
          )}
          <p className="text-[11px] text-muted-foreground">
            You&apos;re told when a friend flags a line, everyone confirms their
            share, a slip needs checking, or someone pays.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
